<template>
  <v-main>
    <v-container fluid>
      <!-- Loading -->
      <v-row v-if="isLoading" class="mt-10" justify="center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-row>

      <template v-else-if="seller">
        <!-- Header -->
        <v-card elevation="2" class="seller-header mb-4">
          <div class="seller-header-banner"></div>
          <v-card-text class="pa-6 pt-0 seller-header-body">
            <v-row no-gutters align="end">
              <v-col cols="12" lg="8">
                <div class="d-flex align-end">
                  <v-avatar size="96" class="seller-avatar mr-4">
                    <v-icon color="primary lighten-2" size="50">mdi-store</v-icon>
                  </v-avatar>
                  <div class="seller-meta pb-1">
                    <h1 class="seller-title mb-1">{{ seller.store_name || seller.full_name || '—' }}</h1>
                    <div class="d-flex align-center flex-wrap mb-2">
                      <span class="role-pill mr-2">
                        <v-icon x-small class="mr-1">mdi-account</v-icon>
                        {{ seller.position || 'Seller' }}
                      </span>
                      <span class="text-caption grey--text text--darken-1">
                        ID: <strong>{{ seller.user_name || seller.id }}</strong>
                      </span>
                    </div>
                    <div class="d-flex align-center flex-wrap">
                      <v-chip :color="seller.is_active ? 'success' : 'error'" small class="mr-2 mb-1">
                        <v-icon left x-small>{{ seller.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                        {{ seller.is_active ? 'Active' : 'Inactive' }}
                      </v-chip>
                      <v-chip :color="seller.is_verified ? 'success' : 'warning'" small class="mr-2 mb-1">
                        <v-icon left x-small>{{ seller.is_verified ? 'mdi-shield-check' : 'mdi-shield-alert' }}</v-icon>
                        {{ seller.is_verified ? 'Verified' : 'Pending Verification' }}
                      </v-chip>
                      <v-chip color="primary lighten-1" outlined small class="mr-2 mb-1" v-if="seller.store_type">
                        <v-icon left x-small>mdi-tag</v-icon>
                        {{ seller.store_type }}
                      </v-chip>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" lg="4" class="d-flex justify-lg-end justify-start mt-lg-0 mt-3">
                <v-btn :to="`/sellers/${$route.params.id}`" color="primary" outlined rounded class="mr-2">
                  <v-icon left small>mdi-pencil</v-icon>Edit Seller
                </v-btn>
                <v-btn @click="$router.back()" color="grey darken-1" outlined rounded>
                  <v-icon left small>mdi-arrow-left</v-icon>Back
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- KPI cards -->
        <v-row class="mb-1">
          <v-col v-for="kpi in kpiCards" :key="kpi.label" cols="12" sm="6" md="4" lg="2">
            <v-card outlined class="kpi-card pa-3 h-100">
              <div class="d-flex align-center mb-1">
                <v-icon :color="kpi.color" small class="mr-2">{{ kpi.icon }}</v-icon>
                <span class="text-caption grey--text text--darken-1">{{ kpi.label }}</span>
              </div>
              <div class="text-h6 font-weight-bold">{{ kpi.value }}</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Seller info + Addresses -->
        <v-row>
          <v-col cols="12" md="6">
            <v-card outlined class="info-card h-100">
              <v-card-title class="card-header">
                <v-icon color="primary" small class="mr-2">mdi-account-circle</v-icon>
                Seller Info
              </v-card-title>
              <v-card-text class="pa-2">
                <div class="info-row" v-if="seller.full_name">
                  <span class="info-label">Full Name</span>
                  <span class="info-value">{{ seller.full_name }}</span>
                </div>
                <div class="info-row" v-if="seller.email">
                  <span class="info-label">Email</span>
                  <span class="info-value">{{ seller.email }}</span>
                </div>
                <div class="info-row" v-if="seller.phone">
                  <span class="info-label">Mobile</span>
                  <span class="info-value">{{ seller.phone }}</span>
                </div>
                <div class="info-row" v-if="seller.store_name">
                  <span class="info-label">Store Name</span>
                  <span class="info-value">{{ seller.store_name }}</span>
                </div>
                <div class="info-row" v-if="seller.store_type">
                  <span class="info-label">Store Type</span>
                  <span class="info-value">{{ seller.store_type }}</span>
                </div>
                <div class="info-row" v-if="seller.gst_no">
                  <span class="info-label">GST Number</span>
                  <span class="info-value">{{ seller.gst_no }}</span>
                </div>
                <div class="info-row" v-if="seller.drug_license_no">
                  <span class="info-label">Drug License</span>
                  <span class="info-value">{{ seller.drug_license_no }}</span>
                </div>
                <div class="info-row" v-if="seller.created_at">
                  <span class="info-label">Registered</span>
                  <span class="info-value">{{ formatDate(seller.created_at) }}</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card outlined class="info-card h-100">
              <v-card-title class="card-header">
                <v-icon color="primary" small class="mr-2">mdi-map-marker</v-icon>
                Addresses
              </v-card-title>
              <v-card-text class="pa-3">
                <div class="address-block">
                  <div class="address-heading">
                    <v-icon x-small color="primary" class="mr-1">mdi-package-variant-closed</v-icon>
                    Pickup Address
                  </div>
                  <div class="address-body" v-if="addresses.pickup">
                    {{ formatAddress(addresses.pickup) }}
                  </div>
                  <div v-else class="text-caption grey--text font-italic">Not provided</div>
                </div>

                <v-divider v-if="addresses.store" class="my-3"></v-divider>

                <div class="address-block" v-if="addresses.store">
                  <div class="address-heading">
                    <v-icon x-small color="primary" class="mr-1">mdi-storefront</v-icon>
                    Store Address
                  </div>
                  <div class="address-body">
                    {{ formatAddress(addresses.store) }}
                  </div>
                </div>

                <!-- Fallback to seller's own state/city/pincode only when there are NO addresses on file -->
                <template v-if="!addresses.pickup && !addresses.store">
                  <v-divider class="my-3" v-if="seller.state || seller.city || seller.pincode"></v-divider>
                  <div class="address-fallback" v-if="seller.state || seller.city || seller.pincode">
                    <span v-if="seller.state"><strong>State:</strong> {{ seller.state }}</span>
                    <span v-if="seller.city"><strong>City:</strong> {{ seller.city }}</span>
                    <span v-if="seller.pincode"><strong>Pincode:</strong> {{ seller.pincode }}</span>
                  </div>
                </template>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>


      </template>
    </v-container>
  </v-main>
</template>

<script>
import axios from 'axios';
import Chart from 'chart.js/auto';

export default {
  name: 'SellerDetailPage',
  data() {
    return {
      isLoading: false,
      seller: null,
      addresses: { pickup: null, store: null, all: [] },
      products: { total: 0, active: 0, inactive: 0, out_of_stock: 0 },
      orders: { total: 0, completed: 0, cancelled: 0, returned: 0, revenue: 0, aov: 0 },
      monthlySales: [],
      chartMode: 'revenue',
      salesChart: null,
    };
  },
  computed: {
    kpiCards() {
      return [
        { label: 'Total Products',  value: this.products.total,        icon: 'mdi-package-variant',          color: 'indigo' },
        { label: 'Active Products', value: this.products.active,       icon: 'mdi-check-circle-outline',     color: 'success' },
        { label: 'Out of Stock',    value: this.products.out_of_stock, icon: 'mdi-package-variant-closed-remove', color: 'error' },
        { label: 'Total Orders',    value: this.orders.total,          icon: 'mdi-cart-outline',             color: 'primary' },
        { label: 'Revenue',         value: this.formatCurrency(this.orders.revenue), icon: 'mdi-currency-inr', color: 'orange darken-1' },
        { label: 'Avg Order Value', value: this.formatCurrency(this.orders.aov),     icon: 'mdi-chart-bar',    color: 'teal' },
      ];
    },
    hasMonthlyData() {
      return this.monthlySales.some(m => (m.orders || 0) > 0 || (m.revenue || 0) > 0);
    },
  },
  watch: {
    chartMode() {
      this.renderChart();
    },
  },
  async mounted() {
    await this.loadStats();
  },
  methods: {
    async loadStats() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(`/sellers/${this.$route.params.id}/stats`);
        if (data.success) {
          const d = data.data;
          this.seller       = d.seller;
          this.addresses    = d.addresses || { pickup: null, store: null, all: [] };
          this.products     = d.products;
          this.orders       = d.orders;
          this.monthlySales = d.monthly_sales || [];
          this.$nextTick(() => this.renderChart());
        } else {
          this.$toast?.error?.(data.message || 'Failed to load seller details');
        }
      } catch (error) {
        console.error('Error loading seller stats:', error);
        this.$toast?.error?.(error?.response?.data?.message || 'Failed to load seller details');
        if (error?.response?.status === 404) {
          this.$router.push('/sellers');
        }
      } finally {
        this.isLoading = false;
      }
    },

    renderChart() {
      if (!this.$refs.salesChart || !this.hasMonthlyData) {
        if (this.salesChart) {
          this.salesChart.destroy();
          this.salesChart = null;
        }
        return;
      }

      const labels = this.monthlySales.map(m => m.label);
      const values = this.monthlySales.map(m =>
        this.chartMode === 'revenue' ? Number(m.revenue || 0) : Number(m.orders || 0)
      );

      if (this.salesChart) {
        this.salesChart.data.labels = labels;
        this.salesChart.data.datasets[0].label = this.chartMode === 'revenue' ? 'Revenue (₹)' : 'Orders';
        this.salesChart.data.datasets[0].data = values;
        this.salesChart.update();
        return;
      }

      this.salesChart = new Chart(this.$refs.salesChart.getContext('2d'), {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: this.chartMode === 'revenue' ? 'Revenue (₹)' : 'Orders',
            data: values,
            fill: true,
            borderColor: '#1976d2',
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
            tension: 0.3,
            pointRadius: 4,
            pointBackgroundColor: '#1976d2',
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true, ticks: { precision: 0 } },
          },
        },
      });
    },

    formatAddress(a) {
      if (!a) return '';
      return [a.flat_number, a.address, a.locality, a.city, a.state, a.pincode]
        .filter(Boolean).join(', ');
    },

    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString('en-IN', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    },
    formatDateTime(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString('en-IN', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    },
    formatCurrency(value) {
      const n = Number(value) || 0;
      return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
  },
  beforeDestroy() {
    if (this.salesChart) this.salesChart.destroy();
  },
};
</script>

<style scoped>
.seller-header { border-radius: 16px !important; overflow: hidden; border: 1px solid #e0e0e0; }
.seller-header-banner {
  height: 90px;
  background:
    radial-gradient(at 80% 20%, rgba(76, 175, 80, 0.15), transparent 60%),
    radial-gradient(at 20% 80%, rgba(33, 150, 243, 0.18), transparent 60%),
    linear-gradient(135deg, #f5f7fa 0%, #eef2f7 100%);
  border-bottom: 1px solid #ecf0f4;
}
.seller-header-body { margin-top: -52px; position: relative; }
.seller-avatar {
  border: 4px solid #fff;
  background: #f5f7fa;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}
.seller-title { font-size: 1.5rem; font-weight: 600; color: #2c3e50; }

.role-pill {
  display: inline-flex; align-items: center;
  background: rgba(25, 118, 210, 0.08); color: #1976d2;
  padding: 2px 10px; border-radius: 999px;
  font-size: 12px; font-weight: 600;
}

.kpi-card {
  border-radius: 12px !important;
  background: #fff;
  transition: all 0.18s ease;
}
.kpi-card:hover {
  border-color: #1976d2;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.08);
}

.info-card { border-radius: 12px !important; }
.card-header {
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  padding: 12px 16px;
  font-size: 0.95rem;
  font-weight: 600;
}

.info-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 10px 14px;
  border-bottom: 1px solid #f1f3f5;
  border-radius: 8px;
  transition: background 0.18s ease;
}
.info-row:last-child { border-bottom: none; }
.info-row:hover { background: #f8fafc; }
.info-label {
  display: inline-flex; align-items: center;
  color: rgba(0, 0, 0, 0.55);
  font-size: 11.5px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.4px;
}
.info-value {
  color: #2c3e50;
  font-size: 14px;
  text-align: right;
  word-break: break-word;
}
.info-value.is-muted { color: rgba(0, 0, 0, 0.38); font-style: italic; }

.address-block { padding: 4px 4px; }
.address-fallback {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 13px;
  color: #2c3e50;
}
.address-fallback strong {
  color: rgba(0, 0, 0, 0.55);
  font-weight: 500;
  margin-right: 4px;
  text-transform: uppercase;
  font-size: 11.5px;
  letter-spacing: 0.4px;
}
.address-heading {
  font-size: 11.5px; font-weight: 600;
  color: rgba(0, 0, 0, 0.55);
  text-transform: uppercase; letter-spacing: 0.4px;
  margin-bottom: 4px;
}
.address-body {
  font-size: 13.5px; color: #2c3e50;
  line-height: 1.45;
}

.chart-wrap { position: relative; height: 320px; }
.empty-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 16px;
  text-align: center;
}

.meta-block {
  background: #f5f7fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px;
  font-size: 12px;
  color: #2c3e50;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 240px;
  overflow: auto;
}
</style>
